import { CdkAccordion } from '@angular/cdk/accordion';
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/components/button';
import { ButtonSwitchComponent } from '@workbuddy/ui/v2/components/button';
import { TooltipModule } from 'primeng/tooltip';
import { commandKeymap, formatChord, humanizeSemanticId, SemanticCommand, ShortcutScope, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import type { FieldError, FieldMode } from '../../core/contracts/field.binding';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { WbPanelComponent, type WbPanelVariant } from '../../core/panel/wb.panel.component';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { buildSectionRenderMap, layoutSurfaceSections, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { SURFACE_DIALOG_CHROME } from '../../core/contracts/surface.dialog.chrome';
import { surfaceCapabilityFields } from '../../core/surface/surface.capability.presenter';
import { WbSurfaceCapabilityRegionComponent } from '../../core/surface/wb.surface.capability.region.component';
import { collectFormHostErrors, formHostDrawsDialogHeader, formHostSubmitIntent, isFormHostBusy, isFormHostSubmitDisabled } from './form.host.state';
import { mountFormHostTheme } from './wb.surface.form.host.theme';

const formShortcutScope: ShortcutScope = { kind: 'panel', panelId: 'surface-form' };

/**
 * SurfaceType.Form host.
 * - submit: dialog-scale borderless (flat) sections + Cancel / submitLabel footer
 * - patch: Manage-like section cards + inplace patch-on-blur (no footer) for formDefinition tabs
 */
@Component({
  selector: 'wb-surface-form-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CdkAccordion, ButtonComponent, ButtonSwitchComponent, TooltipModule, WbPanelComponent, WbSectionComponent, WbSurfaceCapabilityRegionComponent],
  templateUrl: './wb.surface.form.host.component.html',
  styleUrls: ['./wb.surface.form.host.component.less'],
  host: {
    '[attr.aria-busy]': '$busy()',
    '[attr.data-surface]': '"form"',
    '[attr.data-save-strategy]': '$saveStrategy()',
    '[attr.data-column-divider]': '$columnDivider() ? "true" : "false"',
  },
})
export class WbSurfaceFormHostComponent {

  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  // Per-mount value: null when nested or when no delegating dialog frame is around this Form.
  private readonly dialogChrome = inject(SURFACE_DIALOG_CHROME, { optional: true });

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  public constructor() {

    mountFormHostTheme(this.document);
    this.bindSubmitShortcuts();
  }

  protected readonly $submitting = signal(false);
  protected readonly $createMore = signal(false);
  private readonly $submitErrors = signal<readonly FieldError[]>([]);

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$handle().surface.defaultFieldMode);
  protected readonly $saveStrategy = computed(() => this.$handle().surface.saveStrategy);
  protected readonly $columnDivider = computed(() => this.$handle().sectionLayout.columnDivider);
  /** Patch/manage-embedded forms use section cards; submit dialogs stay flat/borderless. */
  protected readonly $panelVariant = computed<WbPanelVariant>(() => this.$saveStrategy() === 'patch' ? 'section' : 'flat');
  protected readonly $showFooter = computed(() => this.$saveStrategy() === 'submit');
  protected readonly $submitLabel = computed(() => this.$handle().compiled.submitLabel ?? 'Save');
  protected readonly $cancelLabel = computed(() => this.$handle().compiled.cancelLabel ?? 'Cancel');
  // The primary button's text while submit runs; the spinner shows either way.
  protected readonly $submitButtonLabel = computed(() => this.$submitting() || this.$page().$saving()
    ? this.$handle().compiled.submittingLabel ?? this.$submitLabel()
    : this.$submitLabel());
  protected readonly $createMoreConfig = computed(() => this.$handle().compiled.createMore);
  // Chords surface where the mouse already is: the Save button and the Create more switch are the
  // only place a first-time user learns that the keyboard can do the same.
  private readonly isMac = /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '');
  protected readonly $submitTooltip = computed(() => `${this.$submitLabel()} · ${formatChord(commandKeymap[SemanticCommand.Submit], this.isMac)}`);
  protected readonly $createMoreTooltip = computed(() => `${this.$submitLabel()} and create another · ${formatChord(commandKeymap[SemanticCommand.SubmitCreateMore], this.isMac)}`);
  protected readonly $busy = computed(() => isFormHostBusy({ initializing: this.$page().$initializing(), submitting: this.$submitting(), saving: this.$page().$saving() }));
  protected readonly $submitDisabled = computed(() => isFormHostSubmitDisabled({ readonly: this.$page().$readonly(), busy: this.$busy(), invalid: this.$page().$invalid(), validating: this.$page().$validating() }));
  protected readonly $errors = computed<readonly FieldError[]>(() => collectFormHostErrors(
    this.$page().$surfaceErrors().map(error => ({ code: error.code, message: error.message })),
    this.$submitErrors(),
  ));

  private readonly $sectionRenderInfoByPath = computed(() => buildSectionRenderMap(this.$handle()));

  protected readonly $bodySections = computed(() => visibleSectionsForPaths(this.$handle().compiled.bodySections, this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $showDialogHeader = computed(() => formHostDrawsDialogHeader(this.dialogChrome ?? null));
  protected readonly $dialogTitle = computed(() => this.dialogChrome?.$title() ?? '');
  protected readonly $capabilityFields = computed(() => surfaceCapabilityFields(this.$handle().compiled, this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $bodyLayout = computed(() => layoutSurfaceSections(this.$bodySections(), this.$handle().sectionLayout.columns, this.$handle().compiled.sectionColumns?.['body']));

  protected closeDialog(): void {

    void this.dialogChrome?.close();
  }

  protected cancel(): void {

    if (this.$busy()) return;
    this.$page().cancel();
  }

  protected async submit(): Promise<void> {

    if (this.$submitDisabled()) return;
    this.$submitting.set(true);
    this.$submitErrors.set([]);
    try {
      const result = await this.$page().submit();
      if (!result.ok) this.$submitErrors.set(result.errors ?? []);
      else if (this.$createMore() && this.$createMoreConfig()) this.resetForNextCreate();
    } finally {
      this.$submitting.set(false);
    }
  }

  /**
   * `⌘↩` saves, `⌘⇧↩` saves and keeps the dialog for the next record. Both fire from inside a text
   * field (that is where the user is when they finish typing), and both are inert on patch forms,
   * which have no footer to submit.
   */
  private bindSubmitShortcuts(): void {

    useShortcutScope(formShortcutScope);
    useShortcuts([SemanticCommand.Submit, SemanticCommand.SubmitCreateMore].map(semanticId => ({
      id: `form.${semanticId}`,
      keys: commandKeymap[semanticId],
      label: humanizeSemanticId(semanticId),
      scope: formShortcutScope,
      allowInInputs: true,
      when: () => this.$showFooter() && !this.$submitDisabled(),
      action: () => {
        const intent = formHostSubmitIntent(semanticId, !!this.$createMoreConfig());
        if (!intent) return;
        if (intent.createMore) this.setCreateMore(true);
        void this.submit();
      },
    })));
  }

  protected setCreateMore(enabled: boolean): void {

    this.$createMore.set(enabled);
    this.$handle().setVariable('formCreateMore', enabled);
  }

  private resetForNextCreate(): void {

    const config = this.$createMoreConfig();
    if (!config) return;
    for (const bind of config.clear) this.$page().field(bind).clear();
    this.$page().reset(this.$page().$model() as Record<string, unknown>);
    queueMicrotask(() => {
      const field = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('wb-field[data-field-bind]')).find(candidate => candidate.dataset['fieldBind'] === config.focus);
      field?.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]:not([tabindex="-1"])')?.focus();
    });
  }
}
